import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, TextField, InputAdornment, Box, Skeleton,
  Typography, Select, MenuItem, Button, Switch, Tooltip,
  TablePagination, Checkbox, Chip, Stack,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import GetApp from "@mui/icons-material/GetApp";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { CSVLink } from "react-csv";

const PositionTable = ({
  positions = [],
  rowCount = 0,
  page = 0, // zero-based
  pageSize = 50,
  onPageChange,
  onPageSizeChange,
  onSearch,
  isLoading = false,
  onEdit,
  onDelete,
  onToggleStatus,
  onBulkActivate,
  onBulkDeactivate,
  onBulkDelete,
}) => {
  const { t } = useTranslation("settings");
  const [localSearch, setLocalSearch] = useState("");
  const [selected, setSelected] = useState(new Set());

  const ordered = useMemo(
    () =>
      positions
        .slice()
        .sort((a, b) => (a.ordem || 0) - (b.ordem || 0) || String(a.nome || "").localeCompare(String(b.nome || ""))),
    [positions]
  );

  const isRowSelected = (id) => selected.has(id);
  const toggleSelect = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const onPageIds = ordered.map((r) => r.id);
  const allOnPageSelected = ordered.length > 0 && ordered.every((r) => selected.has(r.id));
  const toggleSelectAll = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) onPageIds.forEach((id) => next.delete(id));
      else onPageIds.forEach((id) => next.add(id));
      return next;
    });

  const selectedArr = Array.from(selected);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);

  const exportColumns = [
    { field: "nome", headerName: t("shared.name") },
    { field: "usuarios", headerName: t("positionTable.colUsers") },
    { field: "permissoes", headerName: t("positionTable.colPermissions") },
    { field: "status", headerName: t("shared.status") },
  ];
  const selectedData = useMemo(
    () =>
      ordered
        .filter((r) => selected.has(r.id))
        .map((r) => ({
          nome: r.nome ?? "—",
          usuarios: String(r.users_count ?? r.usuarios_count ?? 0),
          permissoes: String(Array.isArray(r.permissions) ? r.permissions.length : r.scopes?.length ?? 0),
          status: r.status ? t("shared.active") : t("shared.inactive"),
        })),
    [ordered, selected, t]
  );
  const exportPDF = () => {
    const doc = new jsPDF();
    doc.text(t("positionTable.exportPdfDocTitle"), 20, 10);
    const headers = exportColumns.map((c) => c.headerName);
    const body = selectedData.map((row) => exportColumns.map((col) => row[col.field]));
    doc.autoTable({ head: [headers], body });
    doc.save("cargos.pdf");
  };
  const csvData = useMemo(
    () =>
      selectedData.map((row) => {
        const o = {};
        exportColumns.forEach((c) => (o[c.headerName] = row[c.field]));
        return o;
      }),
    [selectedData]
  );

  return (
    <Paper variant="outlined">
      {/* Busca */}
      <Box sx={{ p: 1.5 }}>
        <TextField
          fullWidth
          placeholder={t("shared.searchPlaceholder")}
          value={localSearch}
          onChange={(e) => setLocalSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && onSearch?.(localSearch)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><SearchRoundedIcon /></InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Ações em massa padronizadas */}
      {selectedArr.length > 0 && (
        <Box sx={{ mx: 1.5, mb: 2, py: 1, display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
          <Typography variant="subtitle1" color="text.secondary">
            {t("shared.selectedCount", { count: selectedArr.length })}
          </Typography>
          <Button variant="contained" color="primary" startIcon={<GetApp />} onClick={exportPDF} sx={{ textTransform: "uppercase" }}>
            {t("shared.exportPdf")}
          </Button>
          <Button variant="contained" color="primary" startIcon={<GetApp />} sx={{ textTransform: "uppercase" }}>
            <CSVLink data={csvData} filename="cargos.csv" style={{ textDecoration: "none", color: "inherit" }}>
              {t("shared.exportCsv")}
            </CSVLink>
          </Button>
          <Button
            color="error"
            variant="outlined"
            startIcon={<DeleteOutlineRoundedIcon />}
            onClick={() => setConfirmBulkDelete(true)}
            sx={{ textTransform: "uppercase" }}
          >
            {t("shared.deleteSelectedUpper", { count: selectedArr.length })}
          </Button>
        </Box>
      )}

      <Dialog open={confirmBulkDelete} onClose={() => setConfirmBulkDelete(false)}>
        <DialogTitle>{t("shared.deleteSelectedTitle")}</DialogTitle>
        <DialogContent>
          <Typography>
            {t("shared.deleteSelectedConfirm", { count: selectedArr.length })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmBulkDelete(false)}>{t("shared.cancel")}</Button>
          <Button
            color="error"
            onClick={() => {
              onBulkDelete?.(selectedArr);
              setConfirmBulkDelete(false);
            }}
          >
            {t("shared.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={48}>
                <Checkbox
                  checked={allOnPageSelected}
                  indeterminate={!allOnPageSelected && selectedArr.length > 0}
                  onChange={toggleSelectAll}
                />
              </TableCell>
              {/* <TableCell>Ordem</TableCell> */}
              <TableCell>{t("shared.name")}</TableCell>
              <TableCell>{t("positionTable.colUsers")}</TableCell>
              <TableCell>{t("positionTable.colPermissions")}</TableCell>
              <TableCell>{t("shared.status")}</TableCell>
              <TableCell align="right">{t("shared.actions")}</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading ? (
              <>
                {[1, 2, 3, 4, 5, 6].map((row) => (
                  <TableRow key={row}>
                    <TableCell><Skeleton variant="rectangular" width={20} height={20} /></TableCell>
                    <TableCell><Skeleton variant="text" width="80%" height={20} /></TableCell>
                    <TableCell><Skeleton variant="text" width={48} height={20} /></TableCell>
                    <TableCell><Skeleton variant="text" width={80} height={20} /></TableCell>
                    <TableCell><Skeleton variant="text" width={56} height={20} /></TableCell>
                    <TableCell align="right"><Skeleton variant="rounded" width={72} height={28} sx={{ ml: "auto" }} /></TableCell>
                  </TableRow>
                ))}
              </>
            ) : ordered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Box sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t("positionTable.noRoles")}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              ordered.map((row) => {
                const checked = isRowSelected(row.id);
                const permCount = Array.isArray(row.permissions) ? row.permissions.length : (row.scopes?.length || 0);
                return (
                  <TableRow key={row.id} hover selected={checked}>
                    <TableCell>
                      <Checkbox checked={checked} onChange={() => toggleSelect(row.id)} />
                    </TableCell>
                    {/* <TableCell>{row.ordem ?? ""}</TableCell> */}
                    <TableCell>{row.nome}</TableCell>
                    <TableCell>
                      <Chip size="small" label={`${row.users_count ?? row.usuarios_count ?? 0}`} />
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={`${permCount} escopo(s)`} />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Switch
                          size="small"
                          checked={!!row.status}
                          onChange={() => onToggleStatus?.(row)}
                        />
                        <Chip
                          size="small"
                          label={row.status ? "Ativo" : "Inativo"}
                          color={row.status ? "success" : "default"}
                          variant="outlined"
                        />
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => onEdit?.(row)}>
                          <EditRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <IconButton size="small" onClick={() => onDelete?.(row.id)}>
                          <DeleteOutlineRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Paginação */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" color="text.secondary">Linhas por página:</Typography>
          <Select
            size="small"
            value={pageSize}
            onChange={(e) => onPageSizeChange?.(Number(e.target.value))}
          >
            {[10, 25, 50, 100].map((sz) => (
              <MenuItem key={sz} value={sz}>{sz}</MenuItem>
            ))}
          </Select>
        </Box>

        <TablePagination
          component="div"
          count={rowCount}
          page={page}
          rowsPerPage={pageSize}
          onPageChange={(_, newPage) => onPageChange?.(newPage)}
          onRowsPerPageChange={(e) => onPageSizeChange?.(parseInt(e.target.value, 10))}
          labelRowsPerPage="Linhas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} of ${count}`}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Box>
    </Paper>
  );
};

export default PositionTable;
